/**
 * @prettier
 */

// React Packages
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";

// Actions
import { completeStage } from "../../state/actions/submissionStateActions";
import { clearError } from "../../state/actions/navigatorActions";

// Services
import apiService from "../../services/api/api";
import logService from "../../services/shared/logService";
import DataDogService from "../../services/shared/datadogService";
import {
    getCompanyName,
    isSubmissionComplete,
    sanitiseImage,
} from "../../services/shared/helpers";
import { regenerateSession } from "../../services/shared/authenticationService";

//  Components

import Header from "../../components/Header";
import CustomButton from "../../components/CustomButton";
import SubmissionFlowStepper from "../../components/SubmissionFlowStepper";

import Navigator from "../../components/Navigator";
import StageComplete from "../../components/Messages/StageComplete";

// Config
import { ACTION_LABELS } from "../../config/dataDogActionLabels";
import LivenessTestIcon from "./components/LivenessTestIcon";
import LivenessTestPopUpIcon from "./components/LivenessTestPopUpIcon";
import { ModalPopupOverlay } from "../../components/ModalPopupOverlay";
import SelfieSVGIcon from "../../assets/svg/SelfieCaptureIcon.svg";
import { LoadingStateAnimation } from "../../components/ProcessingLayout";

// ID Live Face SDK Development or Prod version
if (process.env.REACT_APP_IDLIVEFACE_VERSION === "dev") {
    import("idlive-face-capture-web-development");
} else {
    import("idlive-face-capture-web");
}

const ACTION = ACTION_LABELS.captureSelfie;

class CaptureSelfiePage extends Component {
    constructor(props) {
        super(props);
        this.primaryFocusRef = React.createRef();
        this._isMounted = false;
        this.initSDKTimeoutId = null;
        this.initializationTimerId = null;
        this.captureElement = null;
        this.sdkEventHandlers = null;
        this.onInitialize = null;
        this.state = {
            loading: true,
            isSDKInitialized: false,
            renderSDK: false,
            initializationTimer: "",
            detectionMessage: "",
            messageClass: "liveness-frame--positive",
            showFrame: false,
            refreshAttempted: false,
            navigation: {
                action: "load",
                props: null,
            },
        };
        this.openCamera = this.openCamera.bind(this);
        this.configureCapture = this.configureCapture.bind(this);
        this.updateMessage = this.updateMessage.bind(this);
        this.submitLivenessImage = this.submitLivenessImage.bind(this);
        this.processImage = this.processImage.bind(this);
        this.fallbackCapture = this.fallbackCapture.bind(this);
        this.initSDK = this.initSDK.bind(this);
        this.brandingColor = `#${this.props.branding.data.company_branding.primary_color}`;
    }

    safeSetState(state, callback) {
        if (this._isMounted) {
            this.setState(state, callback);
        }
    }

    componentDidMount() {
        this._isMounted = true;

        if (this.primaryFocusRef?.current) {
            this.primaryFocusRef.current.focus();
        }

        // Sets document title
        const { t } = this.props;
        document.title = t("idpal_doc_title_liveness");

        // Wait before placing SDK on page
        this.initSDKTimeoutId = setTimeout(this.initSDK, 500);
    }

    componentWillUnmount() {
        this._isMounted = false;

        if (this.initSDKTimeoutId) {
            clearTimeout(this.initSDKTimeoutId);
            this.initSDKTimeoutId = null;
        }

        if (this.initializationTimerId) {
            clearTimeout(this.initializationTimerId);
            this.initializationTimerId = null;
        }

        this.removeCaptureListeners();
    }

    removeCaptureListeners() {
        const capture =
            this.captureElement || document.querySelector("idlive-face-capture");

        if (capture && this.onInitialize) {
            capture.removeEventListener("initialize", this.onInitialize);
            this.onInitialize = null;
        }

        if (capture && this.sdkEventHandlers) {
            Object.entries(this.sdkEventHandlers).forEach(([event, handler]) => {
                capture.removeEventListener(event, handler);
            });
            this.sdkEventHandlers = null;
        }

        this.captureElement = null;
    }

    // Initialize IDR&D SDK, with delay to prepare page content
    initSDK() {
        if (!this._isMounted) {
            return;
        }

        // Fallback timer, if initialization takes too long, fallback to native
        this.initializationTimerId = setTimeout(() => {
            this.fallbackCapture("Initialization timeout");
        }, 12000);

        // Render SDK and store timer, then attach listeners once the element exists
        this.safeSetState(
            {
                renderSDK: true,
                initializationTimer: this.initializationTimerId,
            },
            () => {
                if (!this._isMounted) {
                    return;
                }

                const idliveFaceCapture = document.querySelector(
                    "idlive-face-capture"
                );
                if (idliveFaceCapture) {
                    this.captureElement = idliveFaceCapture;
                    this.onInitialize = () => {
                        if (!this._isMounted) {
                            return;
                        }

                        // Configure Options
                        this.configureCapture(idliveFaceCapture);

                        // Mark as initalized
                        this.safeSetState({
                            loading: false,
                            isSDKInitialized: true,
                        });

                        // Log to DataDog
                        DataDogService.log("IDR&D Camera Initialized");

                        // Clear Timeout
                        clearTimeout(this.initializationTimerId);
                        this.initializationTimerId = null;
                    };
                    idliveFaceCapture.addEventListener(
                        "initialize",
                        this.onInitialize
                    );
                }
            }
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.livenessError !== this.state.livenessError) {
            const { t } = this.props;
            document.title = this.state.livenessError
                ? t("idpal_doc_title_liveness_error")
                : t("idpal_doc_title_liveness");
        }
    }

    fallbackCapture(message) {
        DataDogService.log(
            "Falling back to native ID-Pal Liveness capture. " + message
        );

        // Navigate to native camera fallback
        this.safeSetState({
            navigation: {
                action: "next",
                props: {
                    fallback: true,
                },
            },
        });
    }

    showInstructionModal() {
        if (this.props.isPoaEnabled) {
            if (
                this.props.context === "initial" &&
                this.props.docCompleted &&
                this.props.isDocSubmitted &&
                this.props.poaCompleted &&
                this.props.isPoaDocSubmitted
            ) {
                return true;
            }
        }

        if (!this.props.isPoaEnabled) {
            if (
                this.props.docCompleted &&
                this.props.isDocSubmitted &&
                this.props.context === "initial"
            ) {
                return true;
            }
        }

        return false;
    }

    openCamera() {
        const idliveFaceCapture = document.querySelector("idlive-face-capture");
        idliveFaceCapture.openCamera();
        DataDogService.log("IDR&D Camera Opened");
    }

    /**
     * Configures IDR&D Capture behavior
     * @param {HTMLElement} capture
     */
    configureCapture(capture) {
        if (capture) {
            this.captureElement = capture;
            this.sdkEventHandlers = {
                // Capture is about to occur
                open: () => {
                    this.safeSetState({
                        showFrame: true,
                    });
                },
                // Face has been detected
                faceDetection: event => {
                    if (event.detail[0].errors.length === 0) {
                        this.updateMessage("CAPTURING");
                    } else {
                        this.updateMessage(event.detail[0].errors[0]);
                    }
                },
                // Capture is about to occur
                beforeCapture: () => {
                    this.updateMessage("CAPTURING");
                },
                // Camera was closed
                close: () => {
                    DataDogService.log("IDR&D Camera Closed");
                    this.safeSetState({
                        showFrame: false,
                    });
                    this.updateMessage(null);
                },
                // Capture has occurred
                capture: event => {
                    // Log to Datadog
                    DataDogService.log("IDR&D Liveness Captured");

                    // Clear message, show loader
                    this.updateMessage(null);
                    this.safeSetState({ loading: true });

                    // Send result for processing
                    const { photo, encryptedFile } = event.detail[0];
                    this.processImage(photo, encryptedFile);
                },
                // Error has occurred
                error: event => {
                    // Log Error(s)
                    event.detail.forEach(error =>
                        DataDogService.createError(
                            "IDR&D Liveness error: " + error.message
                        )
                    );
                    this.fallbackCapture("SDK Error");
                },
            };

            Object.entries(this.sdkEventHandlers).forEach(([event, handler]) => {
                capture.addEventListener(event, handler);
            });

            DataDogService.log("IDR&D Camera Configured");
        }
    }

    // Read the image as base64
    processImage(photo, bundle) {
        // If Reusable Profiles, send to legacy endpoint
        if (this.props.context !== "initial") {
            const reader = new FileReader();
            reader.readAsDataURL(photo);

            reader.onloadend = () => {
                if (!this._isMounted) {
                    return;
                }
                this.submitLivenessImage(reader.result);
            };

            // Otherwise send bundle or binary liveness to new endpoint
        } else {
            if (this.props.vendor === "idrnd") {
                this.submitLiveness(bundle, "bundle");
            } else {
                this.submitLiveness(photo, "image");
            }
        }
    }

    // Translates and updates face detection messages for user
    updateMessage(message) {
        if (message === null) {
            this.safeSetState({ detectionMessage: null });
        } else {
            const { t } = this.props;
            const faceDetectionStates = {
                FACE_NOT_FOUND: "idpal_move_face",
                TOO_MANY_FACES: "idpal_too_many_faces",
                FACE_ANGLE_TOO_LARGE: "idpal_move_face",
                PROBABILITY_TOO_SMALL: "idpal_move_face",
                FACE_TOO_SMALL: "idpal_move_closer",
                FACE_CLOSE_TO_BORDER: "idpal_move_face",
                CAPTURING: "idpal_capturing",
            };

            this.safeSetState({
                messageClass:
                    message === "CAPTURING"
                        ? "liveness-frame--positive"
                        : "liveness-frame--negative",

                detectionMessage: t(faceDetectionStates[message]),
            });
        }
    }

    // Send Liveness Binary to Liveness endpoint for validation
    submitLiveness(binary, type) {
        clearError(this);
        apiService
            .postPasssiveLiveness(binary, type)
            .then(response => {
                if (!this._isMounted) {
                    return;
                }
                DataDogService.log(
                    "IDR&D Liveness successfully sent for verification."
                );
                this.props.completeStage("passive_liveness");
                this.safeSetState({
                    navigation: {
                        action: "next",
                    },
                });
            })
            .catch(error => {
                if (!this._isMounted) {
                    return;
                }
                if (error.status === 403 && !this.state.refreshAttempted) {
                    // Session expired
                    regenerateSession(
                        this.submitLiveness,
                        [binary, type],
                        this
                    );
                } else if (error.status === 420) {
                    // Invalid verification
                    DataDogService.log(
                        "ID-Pal Liveness successfully sent, invalid result. Continuing. "
                    );
                    this.props.completeStage("passive_liveness");
                    this.safeSetState({
                        navigation: {
                            action: "next",
                        },
                    });
                } else if (error.status === 429) {
                    // Too many previous attempts
                    DataDogService.log(
                        "ID-Pal Liveness successfully sent, too many previous attempts. Continuing. "
                    );
                    this.props.completeStage("passive_liveness");
                    this.safeSetState({
                        navigation: {
                            action: "next",
                        },
                    });
                } else {
                    // Actual error
                    DataDogService.createError(
                        "Unable to send IDR&D liveness for verification."
                    );
                    this.safeSetState({ loading: false });
                    logService.error(error);
                    this.safeSetState({
                        navigation: {
                            action: "error",
                            props: {
                                retryAction: () =>
                                    this.submitLiveness(binary, type),
                                error: error,
                            },
                        },
                    });
                }
            });
    }

    // Send Base64 Image to API for validation - LEGACY
    submitLivenessImage(base64Image) {
        clearError(this);
        const image = sanitiseImage(base64Image);
        apiService
            .getPassiveLivenessResult(image)
            .then(response => {
                if (!this._isMounted) {
                    return;
                }
                DataDogService.log(
                    "IDR&D Liveness successfully sent for verification at legacy endpoint"
                );
                this.props.completeStage("passive_liveness");
                this.safeSetState({
                    navigation: {
                        action: "next",
                    },
                });
            })
            .catch(error => {
                if (!this._isMounted) {
                    return;
                }
                DataDogService.createError(
                    "Unable to send IDR&D liveness for verification at legacy endpoint."
                );
                this.safeSetState({ loading: false });
                logService.error(error);
                this.safeSetState({
                    navigation: {
                        action: "error",
                        props: {
                            retryAction: () =>
                                this.submitLivenessImage(base64Image),
                            error: error,
                        },
                    },
                });
            });
    }

    render() {
        const { t } = this.props;

        if (isSubmissionComplete()) {
            return (
                <>
                    <Header />
                    <StageComplete
                        message={t("idpal_your_submission_is_complete", {
                            company: getCompanyName(this.props.companyName),
                        })}
                        hideContinue={true}
                        hideButton={true}
                    />
                </>
            );
        }

        return (
            <div className="root-layout-container capture-selfie-page">
                {this.showInstructionModal() && (
                    <ModalPopupOverlay
                        Icon={LivenessTestPopUpIcon}
                        popupTitle={t("idpal_document_upload_completed")}
                        popupText={t(
                            "idpal_liveness_test_instructions_popup-text"
                        )}
                    />
                )}
                <Header />
                <SubmissionFlowStepper />

                {/* Loading state / Processing captured image*/}
                <div
                    style={{
                        display:
                            this.state.loading && this.state.isSDKInitialized
                                ? "block"
                                : "none",
                    }}
                >
                    <LoadingStateAnimation />
                </div>

                {/* Showing instructions to capture image / Initializing SDK state*/}
                <div
                    className={`instructions-content-wrapper ${
                        !this.state.loading ||
                        (this.state.loading && !this.state.isSDKInitialized)
                            ? ""
                            : "hidden"
                    }`}
                >
                    <h1
                        className="instructions-title"
                        ref={this.primaryFocusRef}
                    >
                        {t("idpal_liveness_test_instructions_v2")}
                    </h1>

                    <object
                        className="svg-animation-container"
                        data={SelfieSVGIcon}
                        width="100%"
                    >
                        <LivenessTestIcon fill={this.brandingColor} />
                    </object>

                    <div className="capture-selfie-page-cta-container">
                        <CustomButton
                            id={"capture"}
                            className="btn"
                            isDisabled={!this.state.isSDKInitialized}
                            label={
                                this.state.isSDKInitialized
                                    ? t("idpal_continue")
                                    : t("idpal_loading")
                            }
                            handleClick={this.openCamera}
                            actionDataLabel={ACTION.openFrontCameraButton}
                        />
                    </div>
                </div>

                {this.state.showFrame && (
                    <div
                        className={`liveness-frame__container ${this.state.messageClass}`}
                    >
                        <span className="liveness-frame__message">
                            {this.state.detectionMessage}
                        </span>

                        <div className="liveness-frame__guide"></div>
                    </div>
                )}

                {this.state.renderSDK && (
                    <idlive-face-capture mask_hidden></idlive-face-capture>
                )}

                <Navigator
                    page={"passive_liveness"}
                    action={this.state.navigation.action}
                    propsToPass={this.state.navigation.props}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        isDocSubmitted: state.config.isDocSubmitted,
        isPoaDocSubmitted: state.config.isPoaDocSubmitted,
        docCompleted:
            state.submissionState.submissionState.document_upload.completed,
        poaCompleted:
            state.submissionState.submissionState.poa_upload.completed,
        isPoaEnabled: state.submissionState.screens.poa_upload.enabled,
        vendor: state.submissionState.screens.passive_liveness.vendor,
        context: state.submissionState.screens.passive_liveness.context,
        companyName: state.config.profile.data.company_branding.name,
        branding: state.config.profile,
    };
}

function mapDispatchToProps(dispatch) {
    const actions = bindActionCreators({ completeStage }, dispatch);
    return { ...actions, dispatch };
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(CaptureSelfiePage)
);
